import { useEffect, useRef } from 'react';
import { useCart } from '~Components/CartProvider';
import { useStorage } from '~Components/Storage';
import { useI18n, useSelector } from '~Hooks';
import { useLogger } from '~Hooks/Entities/useLogger';

/** Id used for "Wait <course>" ticket messages. Shared with the manual Wait button. */
export const waitMessageId = (courseId: string) => 'wait' + courseId;

/**
 * Automatically adds "Wait <course>" ticket messages when the cart contains items
 * from more than one course type, so the kitchen holds every course except the
 * first one. Controlled by the `autoAddCourseWait` storage setting.
 *
 * Behaviour:
 * - Courses are ordered by their `order` field. The first course never gets a
 *   wait message; every later course gets one (e.g. starters + mains => "Wait mains").
 * - A message is only auto-added the first time a course appears in the cart. If the
 *   waiter removes it manually it is not re-added while that course stays in the cart.
 * - Messages that this hook added are removed again when their course no longer needs
 *   a hold (course items removed, or it became the only/first course).
 *
 * @param enabled Whether the feature is applicable in the current context (e.g. table mode)
 */
export const useAutoCourseWait = (enabled: boolean) => {
  const cart = useCart();
  const [storage] = useStorage();
  const courseTypes = useSelector((state) => state.courseTypes);
  const { i18n } = useI18n();
  const logger = useLogger('auto-course-wait');

  // Course ids whose wait message this hook added itself. Only these are auto-removed.
  const autoAddedRef = useRef<Set<string>>(new Set());
  // Course ids that have already been handled while in the cart (so manual removals stick).
  const handledRef = useRef<Set<string>>(new Set());

  const active = enabled && storage.autoAddCourseWait;

  useEffect(() => {
    if (!active) return;

    // Collect the distinct course types present among the real items in the cart
    const coursesInCart = new Map<string, { id: string; label: string; order: number }>();
    cart.items.forEach((item) => {
      if (item.isMessage || !item.courseType) return;
      const fromStore = courseTypes.find((ct) => ct.id === item.courseType!.id);
      const ct = fromStore ?? item.courseType;
      coursesInCart.set(ct.id, { id: ct.id, label: ct.label, order: ct.order ?? 0 });
    });

    // Cart is empty (cleared or sent) -> start over for the next order
    if (coursesInCart.size === 0) {
      autoAddedRef.current.clear();
      handledRef.current.clear();
      return;
    }

    const sorted = [...coursesInCart.values()].sort((a, b) => a.order - b.order);
    // Every course except the first should be held by the kitchen
    const coursesToHold = sorted.slice(1);
    const holdIds = new Set(coursesToHold.map((ct) => ct.id));

    // Forget courses that left the cart so a wait is added again if they come back
    handledRef.current.forEach((id) => {
      if (!coursesInCart.has(id)) handledRef.current.delete(id);
    });

    // Remove auto-added waits that are no longer needed
    const idsToRemove: string[] = [];
    autoAddedRef.current.forEach((courseId) => {
      if (!holdIds.has(courseId)) {
        idsToRemove.push(waitMessageId(courseId));
        autoAddedRef.current.delete(courseId);
        handledRef.current.delete(courseId);
      }
    });
    if (idsToRemove.length > 0) {
      logger.info('Removing auto-added wait messages', { ids: idsToRemove });
      cart.removeMessagesFromTicket(idsToRemove);
    }

    // Add waits for courses that need one and haven't been handled yet
    coursesToHold.forEach((ct) => {
      if (handledRef.current.has(ct.id)) return;
      handledRef.current.add(ct.id);

      const alreadyExists = cart.ticketMessages.some((tm) => tm.id === waitMessageId(ct.id));
      if (alreadyExists) return;

      logger.info('Auto-adding wait message for course', {
        courseId: ct.id,
        courseLabel: ct.label,
      });
      autoAddedRef.current.add(ct.id);
      cart.addMessageToTicket(waitMessageId(ct.id), i18n('POS.Wait') + ' ' + ct.label, ct.id);
    });
  }, [active, cart.items, cart.ticketMessages, courseTypes]);
};
